import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper, ArrowRight, Sparkle, Clock, X } from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";

/**
 * FarmersNews — real farm-relevant news feed sourced from the discovery
 * agent's `nature_stories` collection. Merges specialty publishers (Farmers'
 * Almanac, Modern Farmer, Rodale, AgWeb, etc.) with the AI-tagged
 * agriculture / weather / crops / soil / livestock / freshwater buckets.
 */
const FARM_CATEGORIES = [
  { slug: "agriculture", label: "Agriculture" },
  { slug: "weather",     label: "Weather" },
  { slug: "crops",       label: "Crops" },
  { slug: "soil",        label: "Soil" },
  { slug: "livestock",   label: "Livestock" },
  { slug: "freshwater",  label: "Water" },
  { slug: "biodiversity", label: "Biodiversity" },
  { slug: "climate",     label: "Climate" },
  { slug: "food-safety", label: "Food safety" },
  { slug: "policy",      label: "Policy" },
];

const FEATURED_PUBLISHERS = [
  "Farmers' Almanac", "Old Farmer's Almanac", "Modern Farmer",
  "The Rodale Institute", "AgWeb", "Successful Farming", "Down To Earth Agriculture",
];

const CAT_QUERY = FARM_CATEGORIES.map((c) => c.slug).join(",");

export default function FarmersNews() {
  const [active, setActive] = useState(new Set(FARM_CATEGORIES.map((c) => c.slug)));
  const [featured, setFeatured] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const cats = Array.from(active).join(",") || CAT_QUERY;
    http.get(`/nature/stories/list/all?category_in=${cats}&story_type=news_link&limit=60`)
      .then((r) => setStories(r.data.stories || []))
      .catch(() => toast.error("Couldn't load news feed"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [active]);

  // Featured strip is category-independent — fetch once.
  useEffect(() => {
    const featPubs = encodeURIComponent(FEATURED_PUBLISHERS.join(","));
    http.get(`/nature/stories/list/all?publisher_in=${featPubs}&story_type=news_link&limit=6`)
      .then((r) => setFeatured((r.data.stories || []).slice(0, 6)))
      .catch(() => { /* silent — featured hides if it fails */ });
  }, []);

  const toggle = (slug) => {
    const s = new Set(active);
    if (s.has(slug)) s.delete(slug); else s.add(slug);
    // If empty, restore all so we don't show a blank page.
    if (s.size === 0) FARM_CATEGORIES.forEach((c) => s.add(c.slug));
    setActive(s);
  };

  const timeAgo = useMemo(() => {
    return (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
      if (diff < 30 * 86400) return `${Math.round(diff / 86400)}d ago`;
      return d.toLocaleDateString();
    };
  }, []);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-news-page">
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        {/* Hero */}
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Newspaper size={12} weight="fill" className="inline mr-1" /> Farm News
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            Agriculture, weather, water & climate — refreshed every 6 hours
          </h1>
          <p className="mt-2 max-w-2xl text-[#3A3830]">
            AI-curated from Farmers' Almanac, Modern Farmer, Rodale, USDA, Mongabay,
            NOAA, FAO and dozens of trusted publishers. Every card links back to
            the source.
          </p>
        </header>

        {/* Category chips */}
        <section className="mt-6" data-testid="farmers-news-filters">
          <div className="flex flex-wrap gap-1.5">
            {FARM_CATEGORIES.map((c) => {
              const on = active.has(c.slug);
              return (
                <button
                  key={c.slug}
                  onClick={() => toggle(c.slug)}
                  data-testid={`farmers-news-filter-${c.slug}`}
                  className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
                    on ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
                       : "bg-white text-[#3A3830] border-black/15 hover:border-[#1B4332]"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Featured strip — specialty farm publishers */}
        {featured.length > 0 && (
          <section className="mt-10" data-testid="farmers-news-featured">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Sparkle size={12} weight="fill" className="inline mr-1" /> From the almanac shelf
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">
              Fresh from farm publishers
            </h2>
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {featured.map((s) => (
                <NewsCard key={s.id} story={s} timeAgo={timeAgo} featured />
              ))}
            </div>
          </section>
        )}

        {/* Main feed */}
        <section className="mt-12" data-testid="farmers-news-feed">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Latest across all farm topics</div>
          <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">The feed</h2>

          {loading ? (
            <div className="mt-6 text-[#3A3830]">Loading fresh stories…</div>
          ) : stories.length === 0 ? (
            <div className="mt-6 rounded-2xl border-2 border-dashed border-black/10 p-8 text-center text-[#3A3830]">
              No stories match the current filter. Try turning more topics on.
            </div>
          ) : (
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stories.map((s) => <NewsCard key={s.id} story={s} timeAgo={timeAgo} />)}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function NewsCard({ story, timeAgo, featured }) {
  const isNews = story.story_type === "news_link";
  const link = isNews && story.url ? story.url : `/stories/${story.id}`;
  const external = isNews && story.url;
  const Wrap = external ? "a" : Link;
  const props = external
    ? { href: link, target: "_blank", rel: "noopener noreferrer" }
    : { to: link };

  return (
    <Wrap
      {...props}
      data-testid={`farmers-news-card-${story.id}`}
      className={`block rounded-2xl border bg-white overflow-hidden hover:shadow-sm transition ${
        featured ? "border-[color:var(--np-brand,#B85F2A)]/40" : "border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]"
      }`}
    >
      {story.cover_image && (
        <div className="aspect-[16/9] bg-neutral-100 overflow-hidden">
          <img src={story.cover_image} alt={story.headline || story.title}
               className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
          <span>{story.publisher || story.source_domain || "News"}</span>
          {story.publication_date && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={9} weight="fill" /> {timeAgo(story.publication_date)}
              </span>
            </>
          )}
          {external && (<><span>·</span><span className="text-[color:var(--np-brand,#B85F2A)]">External ↗</span></>)}
        </div>
        <div className="mt-1 font-serif-display text-lg leading-tight">
          {story.headline || story.title}
        </div>
        {story.summary && (
          <p className="mt-1 text-sm text-[#3A3830] line-clamp-3">{story.summary}</p>
        )}
        <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          Read <ArrowRight size={10} weight="bold" />
        </div>
      </div>
    </Wrap>
  );
}
